import {
    useState,
    useMemo,
    useEffect,
    useCallback,
    useRef,
} from "react";
import {
    Box,
    Button,
    FormControl,
    MenuItem,
    Paper,
    Select,
    TextField,
    Tooltip,
    Typography,
    IconButton,
    useTheme,
    Autocomplete,
} from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import DG_CONFIG from "../../config/data-grid";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addIdForDataGrid, applyColumnWidths, DG_FORMATTERS, disableHideableColumns, FN_DG_KEY_DOWN } from "../../helpers/data-grid";
import SearchBar from "../../common/components/SearchBar";
import ExportButton from "../../common/components/ExportButton";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DescriptionIcon from "@mui/icons-material/Description";
import { fetchFormulaData } from "../../api/formula-api-service";
import { fetchOutageDataWithFilters } from "../../api/outage-api-service";
import {
    fetchRackPricingData,
    updateRackPricingData,
    updateRackPricingByTGStoredProc,
} from "../../api/rackpricing-api-service";
import { addDays } from "date-fns";
import { fetchRackNoteData } from "../../api/racknote-api-service";
import { toast } from "react-toastify";
import TOASTER from "../../config/toaster";
import logger from "../../helpers/logger";
import { COMMON_MSG } from "../../common/messages";
import { fetchRackPGData } from "../../api/rack-pricing-group-api-service";
import SaveIcon from "@mui/icons-material/Save";
import { useMsal } from "@azure/msal-react";
import UndoIcon from "@mui/icons-material/Undo";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { appInsights } from "../../appInsights";
import useResponsiveConfig from "../../config/responsive-style";
import { getFormattedDate } from "../../common/services/date-service";
import DATE_FORMAT from "../../config/date-config";
import UpdateIcon from "@mui/icons-material/Update";
import { ROUTES } from "../Routes/routes";
import HelpIcon from "@mui/icons-material/Help";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CustomDatePicker from "../../common/components/DatePicker";
import { COLUMN_VISIBILITY_PREF_KEY, COLUMN_WIDTH_PREF_KEY, INPUT_RANGE_TOOLTIP_WARNING_MESSAGE, MAX_INPUT_RACK_AND_BID_PRICING, MIN_INPUT_RACK_AND_BID_PRICING, PAGE_PREF_KEY, SORT_PREF_KEY } from "../../enums";
import { getComponentPreference, setComponentPreference } from "../../common/services/cookie-service";
import { fetchAllocationDailyData, fetchAllocationData } from "../../api/allocation-api-service";
import { PAGE_KEYS } from "../../config/permissions/page-key";
import RoleButton from "../../common/components/RoleButton";
import { BUTTON_TYPES } from "../../config/permissions";
import OutagePopup from "../RackPricing/OutagePopup";
import RackNotePopup from "../RackPricing/RackNotePopup";
import DTNPopup from "../RackPricing/DTNPopup";
import { ButtonWrapper, ControlWrapper, SearchWrapper } from "./style";
import RackPricingWeekendHolidayHelp from "./RackPricingWeekendHolidayHelp";
import { fetchAllRackDefinitions } from "../../api/rackdefinition-api-service";
import { fetchTerminalGroupData } from "../../api/terminalgroup-api-service";
import { RackPricingDGStyle } from "../RackPricing/style";

const SESSION_KEY = "selectedRackPricingWeekendHolidayDate";
const SHOW_EMAILED_SESSION_KEY = "RackPricingWeekendHolidayShowEmailed";

// Format currency for display
const formatCurrency = (value) => {
    if (!value) return "$0.0000";
    try {
        return `$${parseFloat(value).toFixed(4)}`;
    } catch (err) {
        logger.error(`Error formatting currency value: ${value}`, err);
        return "$0.0000";
    }
};

const formatNegativeValue = (value) => {
    if (!value) return "N/A";

    if (value < 0) {
        return (
            <span style={{ color: "red" }}>({formatCurrency(Math.abs(value))})</span>
        );
    }

    return <span>{formatCurrency(value)}</span>;
};

// Helper function to implement PowerApp round-up logic (same as BidPricing)
// RoundUp((value) * 2000, 0)/2000
const roundUpToFourthDecimal = (value) => {
    if (value == null || isNaN(value)) return null;
    return Math.ceil(Number(value) * 2000) / 2000;
};

// Simple edit component to show 4 decimal places with trailing zeros
const DecimalEditCell = ({ params }) => {
    const [value, setValue] = useState(() => {
        // Format initial value to 4 decimal places
        // Handle null, undefined, empty string, and actual numbers
        const val = params.value;
        if (val === null || val === undefined || val === '') {
            return '';
        }
        const parsed = parseFloat(val);
        if (!isNaN(parsed)) {
            return parsed.toFixed(4);
        }
        return '';
    });
    const inputRef = useRef(null);
    const isInitializedRef = useRef(false);
    const lastExternalValueRef = useRef(params.value);
    const userHasEditedRef = useRef(false);

    // Sync external value changes to internal state (when grid data updates externally)
    // Only sync if user hasn't started editing yet, to avoid overwriting user input
    useEffect(() => {
        // Skip if this is the initial mount (handled by useState initializer)
        if (!isInitializedRef.current) return;

        // Skip if user has already started editing - don't overwrite their input
        if (userHasEditedRef.current) return;

        // Check if params.value actually changed from what we last knew
        const externalValueChanged = params.value !== lastExternalValueRef.current;
        if (externalValueChanged) {
            lastExternalValueRef.current = params.value;

            // Update internal state to match new external value
            const val = params.value;
            if (val === null || val === undefined || val === '') {
                setValue('');
            } else {
                const parsed = parseFloat(val);
                if (!isNaN(parsed)) {
                    setValue(parsed.toFixed(4));
                } else {
                    setValue('');
                }
            }
        }
    }, [params.value]);

    // Sync initial value to DataGrid and setup focus - ONLY ON MOUNT
    useEffect(() => {
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;

        // Sync internal state to current params.value in case it changed since useState initializer
        // This handles the edge case where params.value changes before effects run
        const currentValue = params.value;
        lastExternalValueRef.current = currentValue;
        if (currentValue === null || currentValue === undefined || currentValue === '') {
            setValue('');
        } else {
            const parsed = parseFloat(currentValue);
            if (!isNaN(parsed)) {
                setValue(parsed.toFixed(4));
            } else {
                setValue('');
            }
        }

        // Sync the initial value to DataGrid's internal edit state
        // Use the original params.value to preserve exact type/format
        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: currentValue, // Use current value, not stale initial value
        });

        // Auto-focus and select when entering edit mode with sufficient delay
        if (inputRef.current) {
            setTimeout(() => {
                if (inputRef.current && typeof inputRef.current.focus === 'function') {
                    inputRef.current.focus();
                    if (typeof inputRef.current.select === 'function') {
                        inputRef.current.select();
                    }
                }
            }, 50);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (event) => {
        const input = event.target.value;

        // Allow ONLY: digits, optional decimal, optional negative
        const numericRegex = /^-?\d*\.?\d*$/;

        if (input === "" || numericRegex.test(input)) {
            // Mark that user has started editing - prevents external updates from overwriting
            userHasEditedRef.current = true;
            setValue(input);

            // Sync to DataGrid immediately so stopCellEditMode() has the correct value
            // Treat intermediate states (".", "-.", "-") as null to keep display in sync with DataGrid
            const isIntermediateState = input === '.' || input === '-.' || input === '-';
            const numericValue = (input === '' || isIntermediateState) ? null : parseFloat(input);

            // Always sync valid states: empty, intermediate (as null), or valid numbers
            if (input === '' || isIntermediateState || !isNaN(numericValue)) {
                params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: numericValue,
                });
            }
        }
    };

    const handleKeyDown = (event) => {
        // Only handle Escape here - Enter and Tab are handled by onCellKeyDown
        if (event.key === 'Escape') {
            params.api.stopCellEditMode({ id: params.id, field: params.field, ignoreModifications: true });
        }
    };

    return (
        <TextField
            ref={inputRef}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            size="small"
            variant="standard"
            type="text"
            autoFocus
            InputProps={{
                disableUnderline: true,
                style: {
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                }
            }}
            inputProps={{
                style: {
                    caretColor: 'black',
                    cursor: 'text',
                    textAlign: 'center',
                    padding: '0 8px',
                }
            }}
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                '& .MuiInput-root': {
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                },
                '& .MuiInput-underline:before': { borderBottom: 'none' },
                '& .MuiInput-underline:after': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                '& .MuiInputBase-input': {
                    textAlign: 'center',
                    padding: '0 8px',
                    caretColor: 'auto',
                    cursor: 'text',
                },
            }}
        />
    );
};

const RACK_PRICING_WEEKEND_HOLIDAY = "rackPricingWeekendHoliday";
const RACK_PRICING_WEEKEND_HOLIDAY_SEARCH = "rackPricingWeekendHolidaySearch";
const PRICE_GROUP_PREF_KEY = "rackPricingWeekendHolidayPriceGroup";

const RackPricingWeekendHoliday = () => {
    const { accounts } = useMsal();
    const currentAccount = accounts[0] || {};
    const queryClient = useQueryClient();
    const theme = useTheme(); // Track theme changes
    const navigate = useNavigate();

    const getInitialDate = () => {
        const savedDate = sessionStorage.getItem(SESSION_KEY);
        return savedDate ? new Date(savedDate) : addDays(new Date(), 1);
    };

    const initialPageSize = getComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, PAGE_PREF_KEY, DG_CONFIG.ROW_PER_PAGE);
    const initialSorting = getComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, SORT_PREF_KEY);
    const initialColumnVisibility = getComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, COLUMN_VISIBILITY_PREF_KEY);
    const [columnWidths, setColumnWidths] = useState(() => {
        const savedWidths = getComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, COLUMN_WIDTH_PREF_KEY, {});
        logger.log("Rack Pricing Weekend and Holidays: Initial column widths loaded:", savedWidths);
        return savedWidths;
    }); // Run once on mount

    const initialSearch = sessionStorage.getItem(RACK_PRICING_WEEKEND_HOLIDAY_SEARCH);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearch || "");
    const [showEmailed, setShowEmailed] = useState(getComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, SHOW_EMAILED_SESSION_KEY, 0));
    const [selectedPriceGroup, setSelectedPriceGroup] = useState(getComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, PRICE_GROUP_PREF_KEY, "All"));
    const [selectedDate, setSelectedDate] = useState(getInitialDate());
    const [editedPrice, setEditedPrice] = useState({});
    const [selectedRow, setSelectedRow] = useState(null);
    const [updatedRows, setUpdatedRows] = useState([]);
    const [undoCount, setUndoCount] = useState(0);
    const [selectedTerminalGroupKey, setSelectedTerminalGroupKey] = useState(null);
    const [helpOpen, setHelpOpen] = useState(false);
    const userName = accounts?.[0]?.username || accounts?.[0]?.name || "unknown";

    // AUTO-SAVE functionality
    const autoSaveTimeoutRef = useRef(null);
    const apiRef = useGridApiRef();
    const AUTO_SAVE_DELAY = 1500; // 1.5 seconds delay

    const { isLoading, data, refetch } = useQuery({
        queryKey: ["rackPricingData", selectedDate?.toISOString()],
        enabled: !!selectedDate,
        refetchOnWindowFocus: false,
        refetchOnMount: true, // Ensure data refetches when component mounts if cache is stale
        staleTime: 0, // Consider data immediately stale so invalidation works properly
        queryFn: async () => {
            logger.log(
                "Rack Pricing Weekend and Holidays: Fetching data from API - this should show updated margins"
            );
            const formattedDate = getFormattedDate(selectedDate);
            const res = await fetchRackPricingData(formattedDate);
            return res.data || [];
        },
    });

    const { data: allocationsData } = useQuery({
        refetchOnMount: true,
        queryKey: ["allocations"],
        queryFn: async () => {
            const res = await fetchAllocationData({});
            return res.data || [];
        },
    });

    const { data: allocationsDailyData } = useQuery({
        refetchOnMount: true,
        queryKey: ["allocationsDaily"],
        queryFn: async () => {
            const res = await fetchAllocationDailyData({});
            return res.data || [];
        },
    });

    // Get loading flags from each query
    const formulaQuery = useQuery({
        refetchOnWindowFocus: false,
        queryKey: ["formulaData"],
        queryFn: async () => {
            const res = await fetchFormulaData();
            return res.data || [];
        },
    });
    const outageQuery = useQuery({
        refetchOnWindowFocus: false,
        queryKey: ["outageWithFilter", getFormattedDate(selectedDate)],
        queryFn: async () => {
            const res = await fetchOutageDataWithFilters({
                inputDate: getFormattedDate(selectedDate),
            });
            return res.data || [];
        },
    });
    const rackNoteQuery = useQuery({
        refetchOnWindowFocus: false,
        queryKey: ["rackNoteData"],
        queryFn: async () => {
            const res = await fetchRackNoteData();
            return res.data || [];
        },
    });
    const rackPGQuery = useQuery({
        queryKey: ["rackPricingGroupData"],
        queryFn: async () => {
            const res = await fetchRackPGData();
            return res.data || [];
        },
    });
    const terminalGroupQuery = useQuery({
        queryKey: ["terminalGroupData"],
        queryFn: async () => {
            const res = await fetchTerminalGroupData();
            return res.data || [];
        },
    });
    const rackDefinitionQuery = useQuery({
        queryKey: ["rackDefinitionData"],
        enabled: !!terminalGroupQuery.data, // wait for terminal groups
        queryFn: async () => {
            const res = await fetchAllRackDefinitions();
            return res.data || [];
        },
    });

    const { FILTER_BUTTON_STYLE, DATAGRID_STYLE } =
        useResponsiveConfig();

    // Use the isLoading flags only
    const isAllLoading =
        isLoading ||
        formulaQuery.isLoading ||
        outageQuery.isLoading ||
        rackNoteQuery.isLoading ||
        rackPGQuery.isLoading ||
        rackDefinitionQuery.isLoading ||
        terminalGroupQuery.isLoading;

    // Use data from the new queries
    const formulaData = formulaQuery.data;
    const outageData = outageQuery.data;
    const rackNoteData = rackNoteQuery.data;
    const rackPGData = rackPGQuery.data;
    const rackDefinitionData = rackDefinitionQuery.data;
    const terminalGroupData = terminalGroupQuery.data;

    const updateByTerminalGroupMutation = useMutation({
        mutationFn: async ({ data }) => {
            const res = await updateRackPricingByTGStoredProc(data);
            return { res };
        },
        onMutate: async ({ payload }) => {
            const toastId = toast.loading(COMMON_MSG.Processing);
            return { toastId };
        },
        onSuccess: async ({ res }, _variables, context) => {
            if (res.isSuccess) {
                toast.update(context.toastId, {
                    render: res.message,
                    type: "success",
                    isLoading: false,
                    autoClose: TOASTER.AUTO_CLOSE,
                });
                // Refresh the data after successful update
                await refetch();
            }
        },
        onError: (error, _variables, context) => {
            logger.log(error);
            toast.dismiss(context.toastId);
        },
    });

    const saveBtnMutation = useMutation({
        mutationFn: async ({ payload }) => {
            const res = await updateRackPricingData(payload);
            return { res };
        },
        onMutate: async ({ payload }) => {
            const toastId = toast.loading(COMMON_MSG.Processing);
            return { toastId }; // context passed to onSuccess and onError
        },
        onSuccess: async ({ res }, variables, context) => {
            if (res.isSuccess) {
                // Capture the updated rows before clearing them
                const savedRows = [...updatedRows];
                const savedKeys = savedRows.map(r => r.rackPricing_Key);
                logger.log("💾 Save successful. Keys that were saved:", savedKeys);

                setUpdatedRows([]);
                // Refresh the data after successful save
                const refetchResult = await refetch();

                // Log the refetched data for the rows we just saved
                if (refetchResult.data) {
                    savedKeys.forEach(key => {
                        const refetchedRow = refetchResult.data.find(r => r.rackPricing_Key === key || r.rackPricing_Key === Number(key));
                        if (refetchedRow) {
                            logger.log(`📥 Refetched row ${key}:`, {
                                margin: refetchedRow.rackPricing_DefaultMargin,
                                rack: refetchedRow.rackPricing_ActualPrice,
                                marginType: typeof refetchedRow.rackPricing_DefaultMargin,
                                rackType: typeof refetchedRow.rackPricing_ActualPrice
                            });
                        } else {
                            logger.warn(`⚠️ Could not find refetched row for key ${key}`);
                        }
                    });
                }

                // Invalidate RackDefinition cache since margin changes update RackDefinition table
                queryClient.invalidateQueries({
                    queryKey: ["rackDefinitionData"],
                });

                // Clear editedPrice state for successfully saved rows after data refresh
                setEditedPrice((prev) => {
                    const newState = { ...prev };
                    savedRows.forEach((row) => {
                        delete newState[row.rackPricing_Key];
                    });
                    return newState;
                });
                toast.update(context.toastId, {
                    render: res.message,
                    type: "success",
                    isLoading: false,
                    autoClose: TOASTER.AUTO_CLOSE,
                });
            }
        },
        onError: (error, variables, context) => {
            logger.log(error);
            toast.dismiss(context.toastId);
        },
    });

    const terminalGroupOptions = useMemo(() => {
        if (!rackDefinitionData?.length || !terminalGroupData?.length) {
            return [];
        }

        // Build lookup map from TerminalGroup table
        const terminalGroupMap = terminalGroupData.reduce((acc, tg) => {
            acc[tg.terminalGroup_Key] = tg.terminalGroup_Name;
            return acc;
        }, {});

        // Collect unique Terminal Groups from RackDefinition
        const uniqueGroups = new Map();

        rackDefinitionData.forEach((rd) => {
            const tgKey = rd.rackDefinition_TerminalGroup_Key;

            if (!uniqueGroups.has(tgKey)) {
                uniqueGroups.set(tgKey, {
                    terminalGroup_Key: tgKey,
                    terminalGroup_Name:
                        terminalGroupMap[tgKey] || "Unknown Terminal Group",
                });
            }
        });

        return Array.from(uniqueGroups.values());
    }, [rackDefinitionData, terminalGroupData]);

    useEffect(() => {
        handleUpdateDate(selectedDate || addDays(new Date(), 1), false);
    }, []);

    // Refetch data when component mounts or becomes visible to ensure we have latest margin values
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible" && selectedDate) {
                logger.log(
                    "Rack Pricing Weekend and Holidays component became visible - refetching to get latest margin values"
                );
                refetch();
            }
        };

        // Force refetch on component mount to get latest data including margin changes
        if (selectedDate) {
            logger.log(
                "Rack Pricing Weekend and Holidays component mounted - refetching to get latest margin values"
            );
            refetch();
        }

        // Listen for visibility changes (when user navigates back to this tab/component)
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            // AUTO-SAVE: Cleanup timeout on unmount
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, []); // Empty dependency array so it only runs on mount

    const handlePageSizeChange = (newSize) => {
        setComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, PAGE_PREF_KEY, newSize?.pageSize);
    };

    const handleSortChange = (value) => {
        setComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, SORT_PREF_KEY, value);
    };

    const handleColumnVisibilityChange = (value) => {
        setComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, COLUMN_VISIBILITY_PREF_KEY, value);
    };

    const handleColumnResize = (params) => {
        logger.log("Rack Pricing Weekend and Holidays: Full resize params:", params);
        const newWidth = params.width;
        const field = params.field || params.colDef?.field;
        logger.log(`Rack Pricing Weekend and Holidays: Column ${field} resized to ${newWidth}px`);

        if (!field) {
            logger.error("Rack Pricing Weekend and Holidays: No field found in resize params!", params);
            return;
        }

        setColumnWidths(prev => {
            const updated = { ...prev, [field]: newWidth };
            logger.log("Rack Pricing Weekend and Holidays: Saving column widths:", updated);
            setComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, COLUMN_WIDTH_PREF_KEY, updated);
            return updated;
        });
    };

    // Subscribe to column dimension changes to capture auto-fit
    useEffect(() => {
        const api = apiRef.current;
        if (!api) return;

        const handleColumnWidthChange = (params) => {
            const { colDef, width } = params;
            const field = colDef?.field;

            if (!field || !width || width < 50) {
                return;
            }

            logger.log(`Rack Pricing Weekend and Holidays: Column width changed (auto-fit): ${field} to ${width}px`);

            setColumnWidths(prev => {
                // Only update if the width actually changed
                if (prev[field] === width) return prev;

                const updated = { ...prev, [field]: width };
                logger.log("Rack Pricing Weekend and Holidays: Saving auto-fit column width:", updated);

                try {
                    setComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, COLUMN_WIDTH_PREF_KEY, updated);
                    logger.log("Rack Pricing Weekend and Holidays: Auto-fit column width saved successfully");
                } catch (error) {
                    logger.error("Rack Pricing Weekend and Holidays: Failed to save auto-fit column width:", error);
                }

                return updated;
            });
        };

        // Subscribe to column dimension changes
        return api.subscribeEvent('columnWidthChange', handleColumnWidthChange);
    }, [apiRef]);

    const rawColumns = useMemo(() => [
        {
            field: "rackPricing_Date",
            headerName: "Effective Date",
            minWidth: 100,
            flex: 0.4,
            ignoreSearch: true,
            disableColumnMenu: true,
            sortable: true,
            renderCell: (params) => {
                return getFormattedDate(
                    params?.row?.rackPricing_Date,
                    DATE_FORMAT.DATE_MM_DD_YYYY
                );
            },
        },
        {
            field: "outagePopup",
            headerName: "Outage",
            headerClassName: "hide-header",
            width: 40,
            resizable: false,
            ignoreSearch: true,
            sortable: false,
            renderCell: (params) => (
                <OutagePopup
                    row={params.row}
                    outageData={outageData || []}
                    selectedDate={getFormattedDate(
                        selectedDate,
                        DATE_FORMAT.DATE_MM_DD_YYYY
                    )}
                />
            ),
            type: "boolean",
        },
        {
            field: "rackPricing_Terminal_RackDescription",
            headerName: "Terminal",
            minWidth: 120,
            width: 180,
            sortable: true,
            renderCell: (params) => {
                if (!params?.row) return "";

                // Find allocation data for both Gasoline and Diesel for this terminal/date
                const gasolineData = { found: false, gallonsSold: 0, dailyAllocation: 0 };
                const dieselData = { found: false, gallonsSold: 0, dailyAllocation: 0 };

                // Search for Gasoline allocation
                allocationsData?.forEach((a) => {
                    if (a.allocation_Terminal_Key === params.row.rackPricing_Terminal_Key &&
                        a.allocation_FuelType === 'G') {
                        const tempDaily = allocationsDailyData?.find(
                            (ad) =>
                                ad.aD_Allocation_Key === a?.allocation_Key &&
                                new Date(params.row.rackPricing_Date) >= new Date(ad?.aD_EffDate) &&
                                new Date(params.row.rackPricing_Date) <= new Date(ad?.aD_ExpDate)
                        );
                        if (tempDaily) {
                            gasolineData.found = true;
                            gasolineData.gallonsSold = tempDaily.aD_GallonsSold;
                            gasolineData.dailyAllocation = a.allocation_DailyAllocation;
                        }
                    }
                });

                // Search for Diesel allocation
                allocationsData?.forEach((a) => {
                    if (a.allocation_Terminal_Key === params.row.rackPricing_Terminal_Key &&
                        a.allocation_FuelType === 'D') {
                        const tempDaily = allocationsDailyData?.find(
                            (ad) =>
                                ad.aD_Allocation_Key === a?.allocation_Key &&
                                new Date(params.row.rackPricing_Date) >= new Date(ad?.aD_EffDate) &&
                                new Date(params.row.rackPricing_Date) <= new Date(ad?.aD_ExpDate)
                        );
                        if (tempDaily) {
                            dieselData.found = true;
                            dieselData.gallonsSold = tempDaily.aD_GallonsSold;
                            dieselData.dailyAllocation = a.allocation_DailyAllocation;
                        }
                    }
                });

                // If no allocation data found at all
                if (!gasolineData.found && !dieselData.found) {
                    return (
                        <Tooltip
                            title={
                                <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                                    <div style={{ marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '4px' }}>
                                        <strong>{params.value || ""}</strong>
                                    </div>
                                    <div>No Allocation Found</div>
                                </div>
                            }
                            placement="top"
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        fontSize: '16px',
                                        maxWidth: '400px',
                                        padding: '12px 16px'
                                    }
                                }
                            }}
                        >
                            {params.value || ""}
                        </Tooltip>
                    );
                }

                // Calculate variances
                const gasolineVariance = gasolineData.gallonsSold - gasolineData.dailyAllocation;
                const dieselVariance = dieselData.gallonsSold - dieselData.dailyAllocation;

                // Format variance with parentheses for negatives
                const formatVariance = (variance) => {
                    if (variance < 0) {
                        return `(${DG_FORMATTERS.NUMBER(Math.abs(variance))})`;
                    } else if (variance > 0) {
                        return `+${DG_FORMATTERS.NUMBER(variance)}`;
                    } else {
                        return DG_FORMATTERS.NUMBER(variance);
                    }
                };

                // Format tooltip text with terminal description and both fuel types and variance
                const tooltipText = (
                    <div style={{ fontSize: '16px', lineHeight: '1.6' }}>
                        <div style={{ marginBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.3)', paddingBottom: '4px' }}>
                            <strong>{params.value || ""}</strong>
                        </div>
                        {gasolineData.found && (
                            <div>
                                <strong>Gasoline:</strong> {DG_FORMATTERS.NUMBER(gasolineData.gallonsSold)} / {DG_FORMATTERS.NUMBER(gasolineData.dailyAllocation)} / {formatVariance(gasolineVariance)}
                            </div>
                        )}
                        {dieselData.found && (
                            <div>
                                <strong>Diesel:</strong> {DG_FORMATTERS.NUMBER(dieselData.gallonsSold)} / {DG_FORMATTERS.NUMBER(dieselData.dailyAllocation)} / {formatVariance(dieselVariance)}
                            </div>
                        )}
                    </div>
                );

                return (
                    <Tooltip
                        title={tooltipText}
                        placement="top"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    fontSize: '16px',
                                    maxWidth: '400px',
                                    padding: '12px 16px'
                                }
                            }
                        }}
                    >
                        <span>{params.value || ""}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: "rackPricing_FuelProduct_RackDesc",
            headerName: "Product",
            minWidth: 110,
            flex: 0.5,
            sortable: true,
            disableColumnMenu: true,
        },
        {
            field: "rackPricing_SuggestedPrior",
            headerName: "Prior Cost",
            minWidth: 90,
            flex: 0.3,
            ignoreSearch: true,
            sortable: true,
            headerClassName: "prior-header",
            cellClassName: "prior-cell",
            renderCell: (params) =>
                formatCurrency(params.row?.rackPricing_SuggestedPrior),
        },
        {
            field: "rackPricing_PriorMarkup",
            headerName: "Prior Markup",
            minWidth: 110,
            flex: 0.4,
            ignoreSearch: true,
            sortable: true,
            headerClassName: "prior-header",
            cellClassName: "prior-cell",
            renderCell: (params) => {
                const row = params.row;
                if (!row) return "N/A";

                const actualPrior = row.rackPricing_ActualPrior;
                const suggestedPrior = row.rackPricing_SuggestedPrior;

                if (actualPrior === null || suggestedPrior === null) {
                    return "N/A";
                }

                const markup = actualPrior - suggestedPrior;
                return <>{formatNegativeValue(markup)}</>;
            },
        },
        {
            field: "rackPricing_ActualPrior",
            headerName: "Prior Rack",
            minWidth: 90,
            flex: 0.3,
            sortable: true,
            ignoreSearch: true,
            headerClassName: "prior-header",
            cellClassName: "prior-cell",
            renderCell: (params) =>
                formatCurrency(params.row?.rackPricing_ActualPrior),
        },
        {
            field: "rackPricing_SuggestedPrice",
            headerName: "Cost",
            minWidth: 90,
            flex: 0.3,
            ignoreSearch: true,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (params) =>
                formatCurrency(params.row?.rackPricing_SuggestedPrice),
        },
        {
            field: "rackPricing_DefaultMargin",
            headerName: "Margin",
            minWidth: 90,
            flex: 0.3,
            ignoreSearch: true,
            sortable: true,
            disableColumnMenu: true,
            headerAlign: "center",
            editable: true,
            cellClassName: (params) => {
                const key = params.row?.rackPricing_Key;
                const isEdited = key && editedPrice[key]?.margin !== undefined;
                const hasError = Number(params.value) > MAX_INPUT_RACK_AND_BID_PRICING || Number(params.value) < MIN_INPUT_RACK_AND_BID_PRICING;

                return [
                    "editable-cell",
                    isEdited ? "edited-cell" : "",
                    hasError && isEdited ? "error-cell" : "",
                ].join(" ");
            },
            headerClassName: "editable-header",
            type: "number",
            renderEditCell: (params) => {
                // Pass the correct display value to edit cell (respecting editedPrice state)
                const key = params.row?.rackPricing_Key;
                const editedData = editedPrice[key] || {};

                // Use edited margin value if available, otherwise original
                let displayValue = params.value;
                if (editedData.margin !== undefined) {
                    displayValue = editedData.margin;
                }

                return <DecimalEditCell params={{ ...params, value: displayValue }} />;
            },
            // NEW AUTO-SAVE APPROACH - Simple approach with single-click edit
            renderCell: (params) => {
                if (!params.row) return "N/A";

                const key = params.row?.rackPricing_Key;
                const isEdited = key && editedPrice[key]?.margin !== undefined;
                const isError = Number(params.row.rackPricing_DefaultMargin) > MAX_INPUT_RACK_AND_BID_PRICING || Number(params.row.rackPricing_DefaultMargin) < MIN_INPUT_RACK_AND_BID_PRICING;

                // Don't render anything during edit mode - let DataGrid handle it
                if (params.api.getCellMode(params.id, params.field) === "edit") {
                    return null;
                }

                return (
                    <Tooltip
                        title={(isError && isEdited) ? INPUT_RANGE_TOOLTIP_WARNING_MESSAGE : ""}
                        placement="top"
                        arrow
                    >
                        <div
                            style={{
                                cursor: "pointer",
                                width: "100%",
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                padding: "0 8px",
                            }}
                            onClick={(event) => {
                                params.api.startCellEditMode({
                                    id: params.id,
                                    field: params.field,
                                });
                            }}
                        >
                            {formatNegativeValue(params.row?.rackPricing_DefaultMargin)}
                        </div>
                    </Tooltip>
                );
            },

            /* COMMENTED OUT - Original custom renderCell approach
            renderCell: (params) => (
              <div
                style={{
                  cursor: "pointer",
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  padding: "0 16px",
                }}
                onClick={(event) => {
                  // Allow row selection by not stopping propagation
                  params.api.startCellEditMode({
                    id: params.id,
                    field: params.field,
                  });
                }}
              >
                {formatNegativeValue(params.row?.rackPricing_DefaultMargin)}
              </div>
            ),
            */
        },
        {
            field: "rackPricing_Suggested",
            headerName: "Suggested",
            minWidth: 90,
            flex: 0.3,
            ignoreSearch: true,
            sortable: true,
            disableColumnMenu: true,
            renderHeader: (params) => (
                <Tooltip title="Double click the Suggested Price to populate the Rack Price." arrow>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, cursor: "help" }}>
                        <span>Suggested</span>
                        <HelpOutlineIcon
                            sx={{
                                fontSize: "0.75rem",
                                color: "action.active",
                                verticalAlign: "super"
                            }}
                        />
                    </div>
                </Tooltip>
            ),
            renderCell: (params) => {
                const row = params.row;
                if (!row) return "N/A";

                const key = row.rackPricing_Key;
                const editedData = editedPrice[key] || {};

                // Use edited margin if available, otherwise use original margin
                const margin = editedData.margin !== undefined ? editedData.margin : row.rackPricing_DefaultMargin;
                const suggestedPrice = row.rackPricing_SuggestedPrice;

                if (margin === null || suggestedPrice === null) {
                    return "N/A";
                }

                // Calculate suggested value (margin + cost) with edited margin if available
                const rawValue = Number(margin) + Number(suggestedPrice);
                // Apply same PowerApp round-up logic as BidPricing
                const roundedValue = roundUpToFourthDecimal(rawValue);

                return (
                    // Tooltip commented out - header tooltip provides the guidance instead
                    // <Tooltip title="Double click to populate Rack" arrow>
                    <div style={{ width: "100%", height: "100%" }}>
                        {formatCurrency(roundedValue)}
                    </div>
                    // </Tooltip>
                );
            },
            cellClassName: "suggested-cell",
        },
        {
            field: "rackNotePopup",
            headerName: "Rack Note",
            headerClassName: "hide-header",
            width: 40,
            resizable: false,
            sortable: false,
            ignoreSearch: true,
            renderCell: (params) => (
                <RackNotePopup
                    row={params.row}
                    rnNoteData={rackNoteData || []}
                    selectedDate={getFormattedDate(
                        selectedDate,
                        DATE_FORMAT.DATE_MM_DD_YYYY
                    )}
                />
            ),
            type: "boolean",
        },
        {
            field: "rackPricing_ActualPrice",
            headerName: "Rack",
            minWidth: 90,
            flex: 0.3,
            ignoreSearch: true,
            sortable: true,
            disableColumnMenu: true,
            headerAlign: "center",
            editable: true,
            cellClassName: (params) => {
                const key = params.row?.rackPricing_Key;
                const editedData = editedPrice[key] || {};
                const isEdited = key && (editedData.rack !== undefined);

                return [
                    "editable-cell",
                    isEdited ? "edited-cell" : "",
                ].join(" ");
            },
            headerClassName: "editable-header",
            type: "number",
            valueParser: (value) => {
                // If user leaves it empty
                if (value === "" || value === null || value === undefined) {
                    return null;
                }
                return Number(value);
            },
            renderEditCell: (params) => {
                // Pass the correct display value to edit cell (respecting editedPrice state)
                const key = params.row?.rackPricing_Key;
                const editedData = editedPrice[key] || {};

                // Use edited rack value if available, otherwise original
                let displayValue = params.value;
                if (editedData.rack !== undefined) {
                    displayValue = editedData.rack;
                }

                return <DecimalEditCell params={{ ...params, value: displayValue }} />;
            },
            // NEW AUTO-SAVE APPROACH - Simple approach with single-click edit
            renderCell: (params) => {
                if (!params.row) return "N/A";

                // Don't render anything during edit mode - let DataGrid handle it
                if (params.api.getCellMode(params.id, params.field) === "edit") {
                    return null;
                }

                const key = params.row.rackPricing_Key;
                const editedData = editedPrice[key] || {};

                // Get the display value (either edited rack or calculated from edited margin)
                let value;
                if (editedData.rack !== undefined || editedData.rack === null) {
                    // User has directly edited the rack price
                    value = editedData.rack;
                } else {
                    // No edits, use original rack price
                    value = params.row.rackPricing_ActualPrice;
                }

                // Show blank for null values, but still show $0.0000 for actual zero values
                const displayValue = value === null || value === undefined ? "" : formatCurrency(value);

                return (
                    <div
                        style={{
                            cursor: "pointer",
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            padding: "0 8px",
                        }}
                        onClick={(event) => {
                            params.api.startCellEditMode({
                                id: params.id,
                                field: params.field,
                            });
                        }}
                    >
                        {displayValue}
                    </div>
                );
            },

            /* COMMENTED OUT - Original custom renderCell approach
            renderCell: (params) => {
              if (!params.row) return "N/A";
      
              const key = params.row.rackPricing_Key;
              const value = editedPrice[key] ?? params.row.rackPricing_ActualPrice;
              return (
                <div
                  style={{
                    cursor: "pointer",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    padding: "0 16px",
                  }}
                  onClick={(event) => {
                    // Allow row selection by not stopping propagation
                    params.api.startCellEditMode({
                      id: params.id,
                      field: params.field,
                    });
                  }}
                >
                  {formatCurrency(value)}
                </div>
              );
            },
            */
        },
        {
            field: "rackPricing_Markup",
            headerName: "Markup",
            minWidth: 90,
            flex: 0.3,
            ignoreSearch: true,
            sortable: true,
            disableColumnMenu: true,
            renderCell: (params) => {
                const row = params.row;
                if (!row) return "$0.0000";

                const key = row.rackPricing_Key;
                const editedData = editedPrice[key] || {};
                const isEdited = key && (editedData.rack !== undefined || editedData.margin !== undefined);

                // Get the actual rack price (either edited rack or calculated from edited margin)
                let actualPrice;
                if (editedData.rack !== undefined) {
                    // User has directly edited the rack price
                    actualPrice = editedData.rack;
                } else if (editedData.margin !== undefined) {
                    // User has edited the margin, calculate rack from cost + margin
                    const cost = row.rackPricing_SuggestedPrice;
                    if (cost !== null && cost !== undefined) {
                        actualPrice = Number(cost) + Number(editedData.margin);
                    } else {
                        actualPrice = row.rackPricing_ActualPrice;
                    }
                } else {
                    // No edits, use original rack price
                    actualPrice = row.rackPricing_ActualPrice;
                }

                const suggestedPrice = row.rackPricing_SuggestedPrice;

                if (actualPrice === null || suggestedPrice === null) {
                    return "$0.0000";
                }

                const markup = Number(actualPrice) - Number(suggestedPrice);
                const isOutOfRange = markup > MAX_INPUT_RACK_AND_BID_PRICING || markup < MIN_INPUT_RACK_AND_BID_PRICING;
                const showWarning = isOutOfRange && isEdited;

                return (
                    <Tooltip
                        title={showWarning ? INPUT_RANGE_TOOLTIP_WARNING_MESSAGE : ""}
                        placement="top"
                        arrow
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    backgroundColor: "#d32f2f",
                                    "& .MuiTooltip-arrow": {
                                        color: "#d32f2f",
                                    },
                                },
                            },
                        }}
                    >
                        <span
                            style={{
                                color: showWarning ? "#d32f2f" : "inherit",
                                fontWeight: showWarning ? "bold" : "normal",
                                cursor: showWarning ? "help" : "default",
                            }}
                            aria-label={showWarning ? `Warning: ${INPUT_RANGE_TOOLTIP_WARNING_MESSAGE}` : undefined}
                        >
                            {formatNegativeValue(markup)}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: "rackPricing_SalesRep_FirstName",
            headerName: "Price Group",
            minWidth: 90,
            flex: 0.3,
            sortable: true,
        },
        {
            field: "dtnDataPopup",
            headerName: "DTN",
            width: 60,
            resizable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => <DTNPopup row={params.row} />,
            type: "boolean",
        },
        {
            field: "formula_ContractType",
            headerName: "Contract Type",
            headerClassName: "hide-header",
            width: 40,
            resizable: false,
            sortable: false,
            ignoreSearch: false,
            renderHeader: () => (
                <Tooltip title="Formula" arrow>
                    <DescriptionIcon style={{ cursor: "help", fontSize: "1.2rem" }} />
                </Tooltip>
            ),
            renderCell: (params) => {
                const formulaName = params.row.rackPricing_Formula_Name;
                const matchedFormula = formulaData?.find(
                    (f) => f.formula_Name === formulaName
                );
                const contractType = matchedFormula?.formula_ContractType;

                return (
                    <Tooltip title={contractType || "No Formula"} arrow>
                        <MoreHorizIcon style={{ cursor: "pointer" }} />
                    </Tooltip>
                );
            },
            type: "boolean",
        },
    ], [formulaData, outageData, rackNoteData, selectedDate, editedPrice]);

    const columns = useMemo(() => {
        const hideableColumns = disableHideableColumns(rawColumns, [
            "rackPricing_Date",
            "rackPricing_Terminal_RackDescription",
            "rackPricing_FuelProduct_RackDesc",
            "rackPricing_SuggestedPrice",
            "rackPricing_DefaultMargin",
            "rackPricing_Suggested",
            "rackPricing_ActualPrice",
            "rackPricing_Markup",
            "dtnDataPopup",
        ]);

        return applyColumnWidths(hideableColumns, columnWidths);
    }, [rawColumns, columnWidths]);

    const dataWithId = useMemo(() => addIdForDataGrid(data || []), [data]);

    const getGridData = useMemo(() => {
        let filteredGridData = Array.isArray(dataWithId) ? dataWithId : [];

        // Apply search filter
        if (debouncedSearchTerm) {
            filteredGridData = filteredGridData.filter((row) =>
                columns
                    .filter((col) => col.ignoreSearch === undefined)
                    .some((col) =>
                        (row[col.field] ?? "")
                            .toString()
                            .toLowerCase()
                            .includes(debouncedSearchTerm.toLowerCase())
                    )
            );
        }

        //Apply email filter and formula show
        filteredGridData = filteredGridData.filter(
            (o) =>
                (showEmailed == "1"
                    ? o.rackPricing_Emailed === 1
                    : o.rackPricing_Emailed === 0) && o.rackPricing_Formula_Show === 1
        );

        //Apply rack pricing group filter
        if (selectedPriceGroup !== "All") {
            filteredGridData = filteredGridData.filter(
                (o) => o.rackPricing_RackPG_Key === selectedPriceGroup
            );
        }

        //Apply rack pricing terminal group filter
        if (selectedTerminalGroupKey !== null) {
            filteredGridData = filteredGridData.filter(
                (o) => o.rackPricing_TerminalGroup_Key === selectedTerminalGroupKey
            );
        }

        return filteredGridData;
    }, [debouncedSearchTerm, dataWithId, showEmailed, selectedPriceGroup, selectedTerminalGroupKey]);

    const handleShowEmailedChange = (value) => {
        setShowEmailed(value);
        setComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, SHOW_EMAILED_SESSION_KEY, value);
    };

    // AUTO-SAVE: Debounced save function
    const triggerAutoSave = useCallback(() => {
        if (updatedRows.length === 0) return;

        // Clear existing timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        // Set new timeout for auto-save
        autoSaveTimeoutRef.current = setTimeout(async () => {
            logger.log(
                "🔄 Auto-save triggered with",
                updatedRows.length,
                "updated rows"
            );

            try {
                const formattedDate = getFormattedDate(selectedDate);
                const userInfo = {
                    entraId: currentAccount.localAccountId || "",
                    userName: currentAccount.name || currentAccount.username || "",
                    email: currentAccount.username,
                };

                const payload = {
                    inputDate: formattedDate,
                    userInfo: {
                        rackPricing_EntraId: userInfo.email,
                        rackPricing_User: userInfo.userName,
                        rackPricing_ModByEntraId: userInfo.email,
                        rackPricing_ModByUser: userInfo.userName,
                    },
                    updatedRows: updatedRows,
                };

                await saveBtnMutation.mutateAsync({ payload });
                logger.log("✅ Auto-save completed successfully");

                // Auto-save toast notification disabled per user request
                // toast.success("Auto-saved", { autoClose: 1000, hideProgressBar: true, position: "bottom-right" });
            } catch (error) {
                logger.error("❌ Auto-save failed:", error);
                toast.error("Auto-save failed. Please save manually.", {
                    autoClose: TOASTER.AUTO_CLOSE * 2,
                });
            }
        }, AUTO_SAVE_DELAY);
    }, [updatedRows, selectedDate, currentAccount, saveBtnMutation]);

    // AUTO-SAVE: Handle row update with auto-save logic
    const handleProcessRowUpdateWithAutoSave = useCallback(
        (newRow, oldRow) => {
            logger.log("📝 Row update with auto-save:", { oldRow, newRow });

            const key = newRow.rackPricing_Key;

            // Helper to compare values, handling type coercion for numeric fields
            const valuesAreEqual = (newVal, oldVal, field) => {
                // For numeric fields, compare as numbers to avoid type coercion issues
                const numericFields = ['rackPricing_DefaultMargin', 'rackPricing_ActualPrice'];
                if (numericFields.includes(field)) {
                    const newNum = (newVal === null || newVal === undefined || newVal === '') ? null : Number(newVal);
                    const oldNum = (oldVal === null || oldVal === undefined || oldVal === '') ? null : Number(oldVal);
                    // Handle NaN comparisons
                    if (newNum === null && oldNum === null) return true;
                    if (newNum === null || oldNum === null) return false;
                    if (isNaN(newNum) && isNaN(oldNum)) return true;
                    if (isNaN(newNum) || isNaN(oldNum)) return false;
                    // Compare with small tolerance for floating point
                    return Math.abs(newNum - oldNum) < 0.00001;
                }
                return newVal === oldVal;
            };

            // Find what changed
            const changedFields = {};
            Object.keys(newRow).forEach((field) => {
                if (!valuesAreEqual(newRow[field], oldRow[field], field)) {
                    changedFields[field] = newRow[field];
                    // Debug logging for numeric fields
                    if (field === 'rackPricing_DefaultMargin' || field === 'rackPricing_ActualPrice') {
                        logger.log(`📝 Field changed: ${field}`, {
                            oldValue: oldRow[field],
                            newValue: newRow[field],
                            oldType: typeof oldRow[field],
                            newType: typeof newRow[field]
                        });
                    }
                }
            });

            // If nothing changed, return the new row without triggering save
            if (Object.keys(changedFields).length === 0) {
                logger.log("📝 No changes detected, keeping original row");
                return newRow;
            }

            logger.log("📝 Changed fields:", changedFields);

            // Update editedPrice state to track specific field changes
            if (
                changedFields.rackPricing_DefaultMargin !== undefined ||
                changedFields.rackPricing_ActualPrice !== undefined
            ) {
                // Safety check: Don't allow blanking rack price (null/undefined) unless old value was also blank
                // This prevents accidental data loss from false changes
                if (changedFields.rackPricing_ActualPrice !== undefined) {
                    const newVal = changedFields.rackPricing_ActualPrice;
                    const oldVal = oldRow.rackPricing_ActualPrice;

                    if (newVal === null || newVal === undefined || newVal === '') {
                        if (oldVal !== null && oldVal !== undefined && oldVal !== '') {
                            logger.warn(`⚠️ Preventing rack blanking: trying to set ${oldVal} → ${newVal}`);
                            // Remove the blanking attempt from changedFields to keep editedPrice and updatedRows consistent
                            delete changedFields.rackPricing_ActualPrice;
                        }
                    }
                }

                // If no valid changes remain after filtering, return without updating state
                if (Object.keys(changedFields).length === 0) {
                    logger.log("📝 No valid changes after filtering, keeping original row");
                    return newRow;
                }

                setEditedPrice((prev) => {
                    const updatedState = { ...prev };
                    if (!updatedState[key]) {
                        updatedState[key] = {};
                    }

                    // Track specific field edits
                    if (changedFields.rackPricing_DefaultMargin !== undefined) {
                        updatedState[key].margin = changedFields.rackPricing_DefaultMargin;
                        logger.log(`✏️ Setting editedPrice[${key}].margin =`, changedFields.rackPricing_DefaultMargin);
                    }
                    if (changedFields.rackPricing_ActualPrice !== undefined) {
                        updatedState[key].rack = changedFields.rackPricing_ActualPrice;
                        logger.log(`✏️ Setting editedPrice[${key}].rack =`, changedFields.rackPricing_ActualPrice);
                    }

                    return updatedState;
                });
            }

            // Update updatedRows for saving
            // WORKAROUND: API bug sets rack to null if not provided, so always include BOTH margin and rack
            setUpdatedRows((prev) => {
                const existing =
                    prev.find((item) => item.rackPricing_Key === key) || {};
                const merged = {
                    ...existing,
                    rackPricing_Key: key,
                    rackPricing_RackDefinition_Key: newRow.rackPricing_RackDefinition_Key,
                    // Always include both margin and rack to prevent API from nullifying unspecified fields
                    rackPricing_DefaultMargin: changedFields.rackPricing_DefaultMargin !== undefined
                        ? changedFields.rackPricing_DefaultMargin
                        : (existing.rackPricing_DefaultMargin !== undefined ? existing.rackPricing_DefaultMargin : newRow.rackPricing_DefaultMargin),
                    rackPricing_ActualPrice: changedFields.rackPricing_ActualPrice !== undefined
                        ? changedFields.rackPricing_ActualPrice
                        : (existing.rackPricing_ActualPrice !== undefined ? existing.rackPricing_ActualPrice : newRow.rackPricing_ActualPrice),
                };
                const filtered = prev.filter((item) => item.rackPricing_Key !== key);
                return [...filtered, merged];
            });

            // Trigger auto-save
            // triggerAutoSave(); // DISABLED: Auto-save turned off per user request

            return newRow;
        },
        [triggerAutoSave]
    );

    const handleUpdateDate = async (selectedDate, isDateChange) => {
        if (!selectedDate) return;

        logger.log("Update Date event triggered:", selectedDate);

        setSelectedDate(selectedDate);
        sessionStorage.setItem(SESSION_KEY, selectedDate.toISOString());
    };

    const handleSave = async () => {
        appInsights.trackEvent({
            name: `RackPricing_Save`,
            properties: {
                user: userName,
                timestamp_utc: new Date().toISOString(),
                timestamp_local: new Date().toLocaleString(),
                updatedRows,
            },
        });
        const formattedDate = getFormattedDate(selectedDate);
        // Get user info from MSAL
        const userInfo = {
            entraId: currentAccount.localAccountId || "",
            userName: currentAccount.name || currentAccount.username || "",
            email: currentAccount.username,
        };
        let payload = {
            inputDate: formattedDate,
            userInfo: {
                rackPricing_EntraId: userInfo.email,
                rackPricing_User: userInfo.userName,
                rackPricing_ModByEntraId: userInfo.email,
                rackPricing_ModByUser: userInfo.userName,
            },
            updatedRows: updatedRows,
        };

        // Filter out undefined values from updatedRows to prevent blanking fields
        const cleanedUpdatedRows = updatedRows.map(row => {
            const cleaned = {};
            Object.keys(row).forEach(key => {
                if (row[key] !== undefined) {
                    cleaned[key] = row[key];
                }
            });
            return cleaned;
        });

        // Update payload with cleaned rows
        payload.updatedRows = cleanedUpdatedRows;

        // Debug: Log what's being sent to the API - use JSON.stringify to see ACTUAL properties
        logger.log("💾 Saving to API, cleanedUpdatedRows JSON:", JSON.stringify(cleanedUpdatedRows, null, 2));
        cleanedUpdatedRows.forEach((row, idx) => {
            logger.log(`  Row ${idx} actual keys:`, Object.keys(row));
            logger.log(`  Row ${idx} has rackPricing_ActualPrice:`, Object.prototype.hasOwnProperty.call(row, 'rackPricing_ActualPrice'));
        });

        try {
            await saveBtnMutation.mutateAsync({ payload });
        } catch (error) {
            // Check if this is a conflict error
            if (error.conflict) {
                toast.error(
                    error.message ||
                    "Data conflict detected. Please refresh to see the latest changes."
                );
                // Ask if user wants to refresh the data
                if (
                    window.confirm(
                        "Some data has been modified by another user. Would you like to refresh the page to see the latest changes?"
                    )
                ) {
                    // Reset local state and refresh data
                    setUpdatedRows([]);
                    setEditedPrice({});
                    await refetch();
                }
                return; // Prevent further processing
            }
            // If not a conflict error, let the default error handling take over
            logger.error("Error saving data:", error);
        }
    };

    const handleUpdateByTerminalGroup = async () => {
        // Immediate synchronous check to prevent double-clicks
        if (updateByTerminalGroupMutation.isPending) {
            logger.log("Update by terminal group already in progress, ignoring duplicate click");
            return;
        }

        try {
            const formattedDate = getFormattedDate(selectedDate);
            const data = {
                inputDate: formattedDate,
                terminalGroupKey: selectedTerminalGroupKey
            };
            updateByTerminalGroupMutation.mutate({ data });
        } catch (error) {
            logger.error("Error in handleUpdateByTerminalGroup:", error);
        }
    };

    // Handle double click on suggested column to copy to Rack
    const handleSuggestedDoubleClick = (params) => {
        if (!params.row) return;

        // Calculate suggested value (margin + price)
        const margin = params.row.rackPricing_DefaultMargin;
        const suggestedPrice = params.row.rackPricing_SuggestedPrice;

        if (margin === null || suggestedPrice === null) return;

        try {
            const suggested = Number(margin) + Number(suggestedPrice);
            // Apply same PowerApp round-up logic as the Suggested column display
            const roundedSuggested = roundUpToFourthDecimal(suggested);
            const key = params.row.rackPricing_Key;

            // Update the DataGrid row data directly so edit mode shows the correct value
            apiRef.current.updateRows([{
                id: params.id,
                rackPricing_ActualPrice: roundedSuggested,
            }]);

            // AUTO-SAVE APPROACH: Update the Rack price and trigger auto-save
            setEditedPrice((prev) => {
                const updatedState = { ...prev };
                if (!updatedState[key]) {
                    updatedState[key] = {};
                }
                updatedState[key].rack = roundedSuggested;
                return updatedState;
            });

            // Update updatedRows for saving
            setUpdatedRows((prev) => {
                const existing =
                    prev.find((item) => item.rackPricing_Key === key) || {};
                const merged = {
                    ...existing,
                    rackPricing_Key: key,
                    rackPricing_RackDefinition_Key:
                        params.row.rackPricing_RackDefinition_Key,
                    rackPricing_ActualPrice: roundedSuggested,
                };
                const filtered = prev.filter((item) => item.rackPricing_Key !== key);
                return [...filtered, merged];
            });

            // Trigger auto-save
            // triggerAutoSave(); // DISABLED: Auto-save turned off per user request

            /* COMMENTED OUT - Original approach
            handlePriceChange(key, suggested);
            */
        } catch (err) {
            logger.error("Error copying suggested price to rack:", err);
        }
    };

    return (
        <Box sx={{ pt: 2 }}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pb: 2,
                }}
            >
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontSize: "1.25rem",
                        fontWeight: "bold",
                        m: 0,
                    }}
                >
                    Rack Pricing Weekend And Holidays
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Tooltip title="Jump to Rack Confirmation (Weekend/Holiday)" arrow placement="top">
                        <IconButton
                            onClick={() => {
                                logger.log("Navigating to Rack Confirmation Weekend Holiday");
                                navigate(ROUTES.RACK_PRICING.rackConfirmationWeekendHoliday);
                            }}
                            aria-label="Go to Rack Confirmation (Weekend/Holiday)"
                            sx={{ fontSize: "1.5rem", ml: 1 }}
                        >
                            <ArrowForwardIcon sx={{ fontSize: "1.5rem" }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            <Box sx={DATAGRID_STYLE}>
                <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                        }}
                    >
                        <ControlWrapper
                            sx={{
                                ...FILTER_BUTTON_STYLE,
                                width: "100%",
                                flexWrap: { xs: "wrap", sm: "unset" },
                            }}
                        >
                            <SearchWrapper>
                                {/* Effective Date */}
                                <CustomDatePicker
                                    label="Effective Date"
                                    value={selectedDate}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setSelectedDate(newValue);
                                            handleUpdateDate(newValue, true);
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            sx={{ minWidth: 150 }}
                                            size="small"
                                        />
                                    )}
                                    sx={{ minWidth: { xs: "120px", sm: "155px", flexGrow: 1 } }}
                                    disabled={isAllLoading}
                                />

                                {/* Terminal Group Filter */}
                                <Autocomplete
                                    sx={{ minWidth: "200px" }}
                                    options={terminalGroupOptions}
                                    getOptionLabel={(option) => option.terminalGroup_Name}
                                    isOptionEqualToValue={(o, v) =>
                                        o.terminalGroup_Key === v.terminalGroup_Key
                                    }
                                    onChange={(_, selectedOption) => {
                                        setSelectedTerminalGroupKey(selectedOption?.terminalGroup_Key ?? null);
                                    }}
                                    value={
                                        terminalGroupOptions.find(
                                            t => t.terminalGroup_Key === selectedTerminalGroupKey
                                        ) || null
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Terminal Group"
                                            size="medium"
                                        />
                                    )}
                                    disabled={isAllLoading}
                                />

                                {/* Search Bar */}
                                <SearchBar
                                    placeholder={"Search Rack Pricing Weekend And Holidays"}
                                    value={debouncedSearchTerm}
                                    onChange={(val) => {
                                        sessionStorage.setItem(RACK_PRICING_WEEKEND_HOLIDAY_SEARCH, val);
                                        setDebouncedSearchTerm(val);
                                    }}
                                />
                            </SearchWrapper>

                            <ButtonWrapper>
                                {/* Help Button */}
                                <IconButton
                                    onClick={() => setHelpOpen(true)}
                                    aria-label="Help"
                                    color="primary"
                                    sx={{ height: 54, minWidth: 54 }}
                                >
                                    <HelpIcon />
                                </IconButton>

                                {/* Update by Terminal */}
                                <Button
                                    variant="outlined"
                                    onClick={handleUpdateByTerminalGroup}
                                    sx={{
                                        height: 54,
                                        minWidth: 250,
                                        flexGrow: 1,
                                        maxWidth: 250,
                                    }}
                                    aria-label="Update by Terminal Group"
                                    startIcon={<UpdateIcon />}
                                    disabled={
                                        isAllLoading ||
                                        !selectedTerminalGroupKey ||
                                        saveBtnMutation.isPending ||
                                        updateByTerminalGroupMutation.isPending
                                    }
                                >
                                    Update by Terminal Group
                                </Button>

                                {/* Undo Button */}
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setUpdatedRows([]);
                                        setEditedPrice({});
                                        setUndoCount((c) => c + 1);
                                    }}
                                    sx={{
                                        height: 54,
                                        minWidth: 90,
                                        flexGrow: 1,
                                        maxWidth: { xs: "unset", sm: 175 },
                                    }}
                                    aria-label="Undo"
                                    disabled={updatedRows.length === 0 || saveBtnMutation.isPending}
                                    startIcon={<UndoIcon />}
                                >
                                    Undo
                                </Button>

                                {/* Save */}
                                <RoleButton
                                    roleComponent={PAGE_KEYS.RACK_PRICING_WEEKEND_HOLIDAY}
                                    roleButtonType={BUTTON_TYPES.SAVE}
                                    variant="outlined"
                                    onClick={handleSave}
                                    sx={{
                                        height: 54,
                                        minWidth: 90,
                                        flexGrow: 1,
                                        maxWidth: { xs: "unset", sm: 175 },
                                    }}
                                    aria-label="Save"
                                    disabled={
                                        saveBtnMutation.isPending ||
                                        isAllLoading ||
                                        updatedRows.length === 0
                                    }
                                    startIcon={<SaveIcon />}
                                >
                                    {"Save"}
                                </RoleButton>

                                {/* Download Button */}
                                <ExportButton
                                    prefix={"Rack_Pricing"}
                                    data={getGridData}
                                    roleComponent={PAGE_KEYS.RACK_PRICING_WEEKEND_HOLIDAY}
                                    isDisabled={isAllLoading}
                                />
                            </ButtonWrapper>
                        </ControlWrapper>
                    </Box>
                </Paper>

                <Paper elevation={2} sx={{ p: 2 }}>
                    <Box sx={{ ...DG_CONFIG.HEIGHT_STYLES }} onKeyDown={FN_DG_KEY_DOWN}>
                        <DataGrid
                            apiRef={apiRef}
                            key={`${undoCount}-${theme.palette.mode}`} // Include theme in key to prevent re-mount issues
                            rows={getGridData}
                            columns={columns}
                            // NEW AUTO-SAVE APPROACH - Use processRowUpdate with auto-save logic
                            processRowUpdate={handleProcessRowUpdateWithAutoSave}
                            onProcessRowUpdateError={(error) => {
                                logger.error("Row update error:", error);
                                // Return original row on error
                                return error.oldRow;
                            }}
                            initialState={{
                                pagination: {
                                    paginationModel: { pageSize: initialPageSize },
                                },
                                sorting: {
                                    sortModel: initialSorting,
                                },
                                columns: {
                                    columnVisibilityModel: initialColumnVisibility,
                                }
                            }}
                            pageSizeOptions={DG_CONFIG.PAGE_SIZE_OPTIONS}
                            onPaginationModelChange={handlePageSizeChange}
                            onSortModelChange={handleSortChange}
                            onColumnVisibilityModelChange={handleColumnVisibilityChange}
                            onColumnResize={handleColumnResize}
                            disableRowSelectionOnClick={false}
                            disableColumnSorting={true}
                            experimentalFeatures={{ newEditingApi: true }}
                            editMode="cell"
                            // ENHANCED KEYBOARD NAVIGATION - Remove interfering handlers
                            onRowClick={(params) => {
                                setSelectedRow(params);
                                logger.log("🔍 Row clicked, params:", params);
                            }}
                            rowSelectionModel={selectedRow ? [selectedRow.id] : []}
                            onRowSelectionModelChange={(newSelection) => {
                                logger.log("🔍 Row selection changed:", newSelection);
                                if (newSelection.length > 0) {
                                    const rowId = newSelection[0];
                                    const row = getGridData.find((r) => r.id === rowId);
                                    if (row) {
                                        setSelectedRow({ id: rowId, row });
                                    }
                                } else {
                                    setSelectedRow(null);
                                }
                            }}
                            onCellDoubleClick={(params, event) => {
                                if (params.field === "rackPricing_Suggested") {
                                    handleSuggestedDoubleClick(params);
                                }
                            }}
                            // Enhanced keyboard navigation for proper tab order with auto-commit
                            onCellKeyDown={(params, event) => {
                                if (event.key === "Tab") {
                                    event.preventDefault(); // Always prevent default tab behavior

                                    const currentRows = getGridData;
                                    const currentIndex = currentRows.findIndex((row) => row.id === params.id);

                                    logger.log(`🔍 Rack Pricing Weekend and Holidays Tab navigation debug:`, {
                                        currentId: params.id,
                                        currentField: params.field,
                                        currentIndex,
                                        totalRows: currentRows.length,
                                        isShiftTab: event.shiftKey,
                                    });

                                    // First, stop current edit mode to commit the value
                                    if (apiRef.current.getCellMode(params.id, params.field) === "edit") {
                                        apiRef.current.stopCellEditMode({
                                            id: params.id,
                                            field: params.field,
                                        });
                                    }

                                    // Delay to ensure edit is committed before navigating
                                    setTimeout(() => {
                                        let targetId = null;
                                        let targetField = null;

                                        if (!event.shiftKey) {
                                            // Tab forward: Margin -> Rack -> Next Row Margin
                                            if (params.field === "rackPricing_DefaultMargin") {
                                                // From Margin to Rack in same row
                                                targetId = params.id;
                                                targetField = "rackPricing_ActualPrice";
                                                logger.log(`➡️ Margin to Rack (same row)`);
                                            } else if (params.field === "rackPricing_ActualPrice") {
                                                // From Rack to next row's Margin
                                                if (currentIndex < currentRows.length - 1) {
                                                    targetId = currentRows[currentIndex + 1].id;
                                                    targetField = "rackPricing_DefaultMargin";
                                                    logger.log(`➡️ Rack to next row Margin:`, { targetId });

                                                    // Select the new row to highlight it
                                                    const nextRow = currentRows.find((row) => row.id === targetId);
                                                    if (nextRow) {
                                                        setSelectedRow({ id: targetId, row: nextRow });
                                                        if (apiRef.current.setRowSelectionModel) {
                                                            apiRef.current.setRowSelectionModel([targetId]);
                                                        }
                                                    }
                                                } else {
                                                    logger.log(`➡️ At last row, nowhere to go`);
                                                }
                                            }
                                        } else {
                                            // Shift+Tab backward: Rack -> Margin -> Previous Row Rack
                                            if (params.field === "rackPricing_ActualPrice") {
                                                // From Rack to Margin in same row
                                                targetId = params.id;
                                                targetField = "rackPricing_DefaultMargin";
                                                logger.log(`⬅️ Rack to Margin (same row)`);
                                            } else if (params.field === "rackPricing_DefaultMargin") {
                                                // From Margin to previous row's Rack
                                                if (currentIndex > 0) {
                                                    targetId = currentRows[currentIndex - 1].id;
                                                    targetField = "rackPricing_ActualPrice";
                                                    logger.log(`⬅️ Margin to prev row Rack:`, { targetId });

                                                    // Select the previous row to highlight it
                                                    const prevRow = currentRows.find((row) => row.id === targetId);
                                                    if (prevRow) {
                                                        setSelectedRow({ id: targetId, row: prevRow });
                                                        if (apiRef.current.setRowSelectionModel) {
                                                            apiRef.current.setRowSelectionModel([targetId]);
                                                        }
                                                    }
                                                } else {
                                                    logger.log(`⬅️ At first row, nowhere to go`);
                                                }
                                            }
                                        }

                                        // Start edit mode on the target cell if we have one
                                        if (targetId && targetField) {
                                            apiRef.current.startCellEditMode({
                                                id: targetId,
                                                field: targetField,
                                            });
                                        }
                                    }, 10);
                                }

                                if (event.key === "Enter") {
                                    event.preventDefault(); // Always prevent default enter behavior

                                    const currentRows = getGridData;
                                    const currentIndex = currentRows.findIndex((row) => row.id === params.id);

                                    logger.log(`🔍 Enter navigation debug:`, {
                                        currentId: params.id,
                                        currentField: params.field,
                                        currentIndex,
                                        totalRows: currentRows.length,
                                        isShiftEnter: event.shiftKey,
                                    });

                                    // First, stop current edit mode to commit the value
                                    if (apiRef.current.getCellMode(params.id, params.field) === "edit") {
                                        apiRef.current.stopCellEditMode({
                                            id: params.id,
                                            field: params.field,
                                        });
                                    }

                                    // Delay to ensure edit is committed before navigating
                                    setTimeout(() => {
                                        if (!event.shiftKey) {
                                            // Enter: Go to NEXT row's RACK cell
                                            if (currentIndex < currentRows.length - 1) {
                                                const nextRowId = currentRows[currentIndex + 1].id;

                                                apiRef.current.startCellEditMode({
                                                    id: nextRowId,
                                                    field: "rackPricing_ActualPrice", // Always Rack
                                                });

                                                const nextRow = currentRows.find((row) => row.id === nextRowId);
                                                if (nextRow) {
                                                    setSelectedRow({ id: nextRowId, row: nextRow });
                                                    setTimeout(() => {
                                                        if (apiRef.current.setRowSelectionModel) {
                                                            apiRef.current.setRowSelectionModel([nextRowId]);
                                                        }
                                                    }, 20);
                                                    logger.log(`Selected row: ${nextRowId}`);
                                                }
                                            } else {
                                                logger.log(`At last row, nowhere to go`);
                                            }
                                        } else {
                                            // Shift+Enter: Go to PREVIOUS row's RACK cell
                                            if (currentIndex > 0) {
                                                const prevRowId = currentRows[currentIndex - 1].id;

                                                apiRef.current.startCellEditMode({
                                                    id: prevRowId,
                                                    field: "rackPricing_ActualPrice", // Always Rack
                                                });

                                                const prevRow = currentRows.find((row) => row.id === prevRowId);
                                                if (prevRow) {
                                                    setSelectedRow({ id: prevRowId, row: prevRow });
                                                    setTimeout(() => {
                                                        if (apiRef.current.setRowSelectionModel) {
                                                            apiRef.current.setRowSelectionModel([prevRowId]);
                                                        }
                                                    }, 20);
                                                    logger.log(`Selected row: ${prevRowId}`);
                                                }
                                            } else {
                                                logger.log(`At first row, nowhere to go`);
                                            }
                                        }
                                    }, 10);
                                }
                            }}
                            loading={isAllLoading}
                            slotProps={{
                                loadingOverlay: DG_CONFIG.OVERLAY,
                                cell: {
                                    tabIndex: 0, // Make cells tabbable for keyboard navigation
                                },
                            }}
                            sx={RackPricingDGStyle}
                        />
                    </Box>
                    <Box
                        sx={{
                            mt: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                        }}
                    >
                        {/* Empty left side for future content like wet moves */}
                        <Box />

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                pt: { xs: 2 },
                                flexDirection: { xs: "column", sm: "unset" },
                            }}
                        >
                            {/* Show Emailed Dropdown */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ paddingRight: "5px", minWidth: 120 }}
                                >
                                    Show Emailed?
                                </Typography>
                                <FormControl sx={{ minWidth: 100 }} size="small">
                                    <Select
                                        value={showEmailed}
                                        onChange={(e) => handleShowEmailedChange(e.target.value)}
                                        sx={{
                                            color: showEmailed == 1 ? "green" : "red",
                                            fontWeight: "bold",
                                        }}
                                    >
                                        <MenuItem
                                            value="0"
                                            sx={{ color: "red", fontWeight: "bold" }}
                                        >
                                            False
                                        </MenuItem>
                                        <MenuItem
                                            value="1"
                                            sx={{ color: "green", fontWeight: "bold" }}
                                        >
                                            True
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Price Group Dropdown */}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    width: "100%",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ paddingRight: "5px", minWidth: 120 }}
                                >
                                    Price Group
                                </Typography>
                                <FormControl sx={{ minWidth: 100 }} size="small">
                                    <Select
                                        value={selectedPriceGroup}
                                        onChange={(e) => {
                                            setSelectedPriceGroup(e.target.value);
                                            setComponentPreference(RACK_PRICING_WEEKEND_HOLIDAY, PRICE_GROUP_PREF_KEY, e.target.value);
                                        }
                                        }
                                    >
                                        <MenuItem value="All">All</MenuItem>
                                        {rackPGData
                                            ?.sort((a, b) => {
                                                const orderDiff = a.rackPG_Order - b.rackPG_Order;
                                                return orderDiff !== 0
                                                    ? orderDiff
                                                    : a.rackPG_Name.localeCompare(b.rackPG_Name);
                                            })
                                            .map((o) => (
                                                <MenuItem key={o.rackPG_Key} value={o.rackPG_Key}>
                                                    {o.rackPG_Name}
                                                </MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            {helpOpen && (
                <RackPricingWeekendHolidayHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
            )}
        </Box>
    );
};

export default RackPricingWeekendHoliday;
